"use client";

import { useEffect, useRef, useState } from "react";
import InsideCommunity from "./InsideCommunity";
import EmpoweringStartups from "./EmpoweringStartups";
import EnterPrise from "./Enterprise";
import { getClientList } from "@/lib/ApiClient";
import {
  MappedClientData,
  mapClientListResult,
} from "./clientData";

const PAGE_LIMIT = 8;

interface ClientsSectionsProps {
  initialData: MappedClientData | null;
}

/**
 * Client wrapper for the three data-driven sections of /our-clients. The first
 * page of data is fetched on the server and passed in, so the initial HTML
 * contains the client logos; this component only refetches on pagination.
 */
export default function ClientsSections({ initialData }: ClientsSectionsProps) {
  const [loading, setLoading] = useState(initialData === null);
  const [error, setError] = useState<string | null>(
    initialData === null ? "Failed to load client data." : null,
  );
  const [data, setData] = useState<MappedClientData | null>(initialData);

  useEffect(() => {
    // initialData should be fetched by the server. If not, fetch it once.
    if (initialData !== null) return;

    let cancelled = false;
    async function fetchClientData() {
      try {
        setLoading(true);
        setError(null);

        const result = await getClientList(0, PAGE_LIMIT);
        const mapped = mapClientListResult(result);
        if (cancelled) return;

        if (mapped) {
          setData(mapped);
        } else {
          console.warn("Not all categories returned");
          setError("Failed to load client data.");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching client data:", err);
        setError("Failed to load client information.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchClientData();
    return () => {
      cancelled = true;
    };
  }, [initialData]);

  return (
    <>
      <InsideCommunity
        brandsData={data?.brandsItems ?? []}
        categoryData={data?.brandsCategory ?? null}
        loading={loading}
        error={error}
        showPagination={true}
      />

      <EmpoweringStartups
        startupData={data?.startupsItems ?? []}
        categoryData={data?.startupsCategory ?? null}
        loading={loading}
        error={error}
      />

      <EnterPrise
        enterpriseItems={data?.enterpriseItems ?? []}
        categoryData={data?.enterpriseCategory ?? null}
        loading={loading}
        error={error}
        showPagination={true}
      />
    </>
  );
}
